import { DataTable, FAIcon } from '@/components'
import type { Module, ResponsibleUserForm } from '@/modules/companies'
import { EditResponsibleUserModal } from '@/modules/companies'
import type { ResponsibleUserNode } from '@/modules/users'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import { UserThumbOrInitials } from '@/modules/users/UserThumbOrInitials'
import { getUserLabel } from '@/modules/users/util'
import { useModuleConfig } from '@/utils/hooks'
import type { ColumnDef } from '@tanstack/react-table'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback, useMemo } from 'react'
import { BrowserView } from 'react-device-detect'
import { useModuleConfigMutations } from '../util/mutations.hooks'
import { AddButton, Section } from './common'

interface ResponsibleUserTableData {
  id: string
  user: ResponsibleUserNode
}

export const ModuleResponsibleUsers: React.FC<{ module: Module }> = ({
  module,
}) => {
  const translations = useTranslate({
    update: 'common.update',
    add: 'common.add',
    responsibleUsers: 'common.responsible-users',
    user: 'common.user',
    noResults: 'common.no-results',

    numUsers: ['settings.absence.responsible-users.num-users', { n: 0 }],
  })

  const addPrompt = usePrompt()

  const { moduleConfig } = useModuleConfig(module)

  const responsibleUsers = useMemo(
    () => moduleConfig?.responsibleUsers ?? [],
    [moduleConfig]
  )

  const responsibleUserIds = useMemo(
    () => responsibleUsers.map(user => user.id),
    [responsibleUsers]
  )

  const { patch: patchModuleConfig } = useModuleConfigMutations()

  const handleCreatePrompt = useCallback(async () => {
    const { data } = await addPrompt<ResponsibleUserForm | null>(resolve => (
      <EditResponsibleUserModal responsibleUser={null} onSubmit={resolve} />
    ))
    if (!moduleConfig) return
    if (data === null || data.userId === null) return

    await patchModuleConfig({
      variables: {
        id: moduleConfig.id,
        input: {
          responsibleUsers: [...responsibleUserIds, data.userId],
        },
      },
    })
  }, [addPrompt, moduleConfig, patchModuleConfig, responsibleUserIds])

  const handleDeleteResponsible = useCallback(
    async (userId: string) => {
      if (!moduleConfig) return
      const updatedResponsibleUsers = responsibleUserIds.filter(
        id => id !== userId
      )
      await patchModuleConfig({
        variables: {
          id: moduleConfig.id,
          input: {
            responsibleUsers: updatedResponsibleUsers,
          },
        },
      })
    },
    [moduleConfig, patchModuleConfig, responsibleUserIds]
  )

  const tableColumns = useMemo<ColumnDef<ResponsibleUserTableData>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '',
        enableSorting: false,
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return <UserThumbOrInitials user={user} />
        },
      },
      {
        accessorKey: 'user',
        header: translations.user,
        enableSorting: true,
        sortingFn: (a, b) =>
          a.original.user.lastName.localeCompare(b.original.user.lastName),
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <UserProfileLink user={user} userNameTransform={getUserLabel} />
          )
        },
      },
      {
        accessorKey: 'delete',
        header: '',
        enableSorting: false,
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteResponsible(user.id)}
            />
          )
        },
      },
    ],
    [handleDeleteResponsible, translations.user]
  )

  const tableData = useMemo<ResponsibleUserTableData[]>(
    () =>
      responsibleUsers.map(user => ({
        id: user.id,
        user,
      })),
    [responsibleUsers]
  )

  return (
    <Section
      header={translations.responsibleUsers}
      actions={
        <BrowserView>
          <AddButton onClick={handleCreatePrompt} />
        </BrowserView>
      }
    >
      <DataTable
        columns={tableColumns}
        data={tableData}
        pagination={{
          itemsText: n => translations.numUsers({ n }),
        }}
      />
    </Section>
  )
}
